import { useState } from "react";
import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";

export interface FAQEntry {
  q: string;
  a: string;
}

interface FAQSectionProps {
  badge?: string;
  title?: ReactNode;
  description?: string;
  faqs?: FAQEntry[];
  sectionId?: string;
}

const defaultFaqs: FAQEntry[] = [
  { q: "What is eSchool.et?", a: "eSchool.et is Ethiopia's #1 cloud-based Educational Institution Management Software. It helps schools and colleges manage fees, exams, attendance, staff, salaries, library, hostels, communication, and much more—all in one secure platform." },
  { q: "Who can use eSchool.et?", a: "We provide solutions for Schools (Grades 1–12), Private Colleges, and Private Universities. Our platform is flexible enough to handle the needs of both small schools and large higher-education institutions." },
  { q: "How is eSchool.et different from other systems?", a: "Designed specifically for Ethiopian institutions. Covers everything from attendance to salaries in one platform. Mobile apps for parents, students, and teachers. Cloud-based, secure, and scalable. We onboard only 10 institutions per year to guarantee quality and premium support." },
  { q: "Do we need servers or special equipment?", a: "No. eSchool.et is fully cloud-based. You just need an internet connection and any device (computer, tablet, or smartphone)." },
  { q: "How do parents and students benefit?", a: "Through our mobile app, parents and students receive real-time updates on attendance, results & exams, fee reminders, and announcements. This makes communication fast, transparent, and reliable." },
  { q: "Is the system easy for teachers and staff?", a: "Yes! The interface is simple and intuitive. Teachers can take attendance in seconds, HR can process salaries quickly, and administrators can generate reports instantly. We also provide training during onboarding." },
  { q: "How much does it cost?", a: "Our pricing is based on the number of active users (students, staff, and administrators). This makes it flexible and fair—you only pay for what you actually use. Since we limit ourselves to 10 institutions per year, every client receives personalized attention and premium support." },
];

const TriangleIcon = ({ className }: { className?: string }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    style={{ display: "block" }}
  >
    <polygon points="8 5 18 12 8 19 8 5" />
  </svg>
);

const FAQSection = ({
  badge = "Frequently Asked Questions",
  title = <>Got Questions?<br />We've Got Answers.</>,
  description = "Everything you need to know about the product and billing. Can't find the answer you're looking for? Please chat to our friendly team.",
  faqs = defaultFaqs,
  sectionId = "frequently-asked-questions",
}: FAQSectionProps = {}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  return (
    <section id={sectionId} aria-labelledby={`${sectionId}-title`} className="py-24 bg-white dark:bg-slate-950 relative overflow-hidden">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <div className="absolute top-0 right-0 -m-32 w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 lg:px-8 max-w-7xl relative z-10 pt-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1.5fr] gap-12 lg:gap-16 items-start">

          <div className="lg:sticky lg:top-32 lg:pr-8">
            <div className="inline-flex items-center rounded-3xl border border-slate-200 bg-white px-5 py-2 mb-8 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <span className="text-sm font-bold text-slate-800 tracking-wide dark:text-slate-100">
                {badge}
              </span>
            </div>

            <h2 id={`${sectionId}-title`} className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-slate-950 dark:text-white leading-[1.1] tracking-tight mb-8 mt-0">
              {title}
            </h2>

            <p className="text-lg text-slate-600 dark:text-slate-300 font-normal leading-relaxed max-w-[420px]">
              {description}
            </p>
          </div>

          <div className="w-full flex flex-col gap-4">
            {faqs.map((faq, index) => (
              <div key={faq.q} className="border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden bg-white dark:bg-slate-900 shadow-[0_2px_8px_-6px_rgba(0,0,0,0.1)]">
                <button
                  type="button"
                  aria-expanded={openIndex === index}
                  aria-controls={`${sectionId}-answer-${index}`}
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="group flex min-h-14 w-full items-center gap-5 p-6 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring dark:hover:bg-slate-800 motion-reduce:transition-none"
                >
                  <div
                    className="flex-shrink-0 text-slate-500 group-hover:text-primary transition-transform duration-300 motion-reduce:transition-none"
                    style={{ transform: openIndex === index ? 'rotate(90deg)' : 'rotate(0deg)' }}
                  >
                    <TriangleIcon className="w-3.5 h-3.5" />
                  </div>
                  <span className="font-heading font-bold text-base text-slate-900 dark:text-slate-100 group-hover:text-primary transition-colors">
                    {faq.q}
                  </span>
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div id={`${sectionId}-answer-${index}`} className="px-6 pb-7 pl-[3.75rem] text-[15px] text-slate-600 dark:text-slate-300 leading-relaxed bg-white dark:bg-slate-900">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default FAQSection;
